import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/pro-solid-svg-icons";
import { Icon, Menu } from "@intility/bifrost-react";
import Accordion from "@intility/bifrost-react/Accordion";
import { useEffect } from "react";
import { Link, Navigate, NavLink, useLocation, useParams } from "react-router";
import { type PageCrumb, PageLayout } from "../../components/layout/PageLayout";
import { TableOfContents, TocList } from "../../components/TableOfContents";
import {
  DOC_SECTIONS,
  type DocEntry,
  type DocSection,
  type DocSectionKey,
  FIRST_DOC_SECTION,
  FIRST_DOC_SLUG,
  getDoc,
  getSection,
} from "./content";
import { DocSearch } from "./DocSearch";
import { Markdown } from "./Markdown";
import { extractToc, groupToc } from "./toc";

function splitTitle(raw: string): { title: string; body: string } {
  const nl = raw.indexOf("\n");
  const first = (nl === -1 ? raw : raw.slice(0, nl)).trim();
  if (first.startsWith("# ")) {
    return {
      title: first.slice(2).trim(),
      body: raw.slice(nl + 1).replace(/^\s+/, ""),
    };
  }
  return { title: "", body: raw };
}

function docPath(sectionKey: DocSectionKey, slug: string) {
  return `/docs/${sectionKey}/${slug}`;
}

function DocTabBar({ section }: { section: DocSection }) {
  return (
    <div
      style={{
        background: "var(--bfc-base-3)",
        borderRadius: 8,
        padding: "8px",
      }}
    >
      <Menu horizontal>
        {section.entries.map((d) => (
          <NavLink key={d.slug} to={docPath(section.key, d.slug)}>
            <Menu.Item>
              <Icon icon={d.icon} marginRight />
              {d.navLabel}
            </Menu.Item>
          </NavLink>
        ))}
      </Menu>
    </div>
  );
}

function findNeighbours(
  sectionKey: DocSectionKey,
  slug: string,
): {
  prev?: DocEntry & { sectionKey: DocSectionKey };
  next?: DocEntry & { sectionKey: DocSectionKey };
} {
  const allEntries = DOC_SECTIONS.flatMap((s) =>
    s.entries.map((e) => ({ ...e, sectionKey: s.key })),
  );
  const idx = allEntries.findIndex(
    (e) => e.sectionKey === sectionKey && e.slug === slug,
  );
  return {
    prev: allEntries[idx - 1],
    next: allEntries[idx + 1],
  };
}

export default function Documentation() {
  const { section, slug } = useParams<{
    section: DocSectionKey;
    slug: string;
  }>();
  const location = useLocation();
  const doc = section && slug ? getDoc(section, slug) : undefined;

  useEffect(() => {
    if (!doc) return;
    if (location.hash) {
      const el = document.getElementById(
        decodeURIComponent(location.hash.slice(1)),
      );
      if (el) {
        el.scrollIntoView();
        return;
      }
    }
    let node = document.getElementById("doc-top")?.parentElement ?? null;
    while (node) {
      const overflowY = getComputedStyle(node).overflowY;
      if (
        (overflowY === "auto" || overflowY === "scroll") &&
        node.scrollHeight > node.clientHeight
      ) {
        node.scrollTo({ top: 0 });
        return;
      }
      node = node.parentElement;
    }
    window.scrollTo({ top: 0 });
  }, [doc, location.hash]);

  if (!doc || !section || !slug) {
    return <Navigate to={docPath(FIRST_DOC_SECTION, FIRST_DOC_SLUG)} replace />;
  }

  const sectionMeta = getSection(section);
  if (!sectionMeta) {
    return <Navigate to={docPath(FIRST_DOC_SECTION, FIRST_DOC_SLUG)} replace />;
  }

  const { title, body } = splitTitle(doc.raw);
  const tocSections = groupToc(extractToc(body));
  const { prev, next } = findNeighbours(section, slug);

  const breadcrumbs: PageCrumb[] = [
    { label: "Home", to: "/" },
    { label: "Documentation", to: "/docs" },
    { label: sectionMeta.label },
    ...(sectionMeta.entries.length > 1 ? [{ label: doc.navLabel }] : []),
  ];

  return (
    <PageLayout
      breadcrumbs={breadcrumbs}
      title={
        sectionMeta.entries.length > 1
          ? `${sectionMeta.label}: ${doc.navLabel}`
          : title
      }
      level={2}
      maxWidth={1120}
      actions={<DocSearch />}
    >
      <div
        id="doc-top"
        style={{
          scrollMarginTop: 96,
        }}
      >
        {sectionMeta.entries.length > 1 && <DocTabBar section={sectionMeta} />}

        <div
          style={{
            display: "flex",
            gap: 24,
            alignItems: "flex-start",
            marginTop: sectionMeta.entries.length > 1 ? 24 : 0,
          }}
        >
          <div className="bf-highlight-target" style={{ flex: 1, minWidth: 0 }}>
            {tocSections.length > 0 && (
              <Accordion className="to-large" variant="styled">
                <Accordion.Item title="On this page">
                  <TocList sections={tocSections} />
                </Accordion.Item>
              </Accordion>
            )}

            <Markdown source={body} section={section} />

            <nav
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 16,
                marginTop: 8,
                borderTop: "1px solid var(--bfc-base-c-dimmed)",
                paddingTop: 16,
              }}
            >
              {prev ? (
                <Link
                  className="bf-title-link"
                  to={docPath(prev.sectionKey, prev.slug)}
                >
                  <Icon icon={faChevronLeft} marginRight />
                  {prev.navLabel}
                </Link>
              ) : (
                <span />
              )}
              {next ? (
                <Link
                  className="bf-title-link"
                  to={docPath(next.sectionKey, next.slug)}
                >
                  {next.navLabel}
                  <Icon icon={faChevronRight} marginLeft />
                </Link>
              ) : (
                <span />
              )}
            </nav>
          </div>

          {tocSections.length > 0 && (
            <TableOfContents sections={tocSections} topId="doc-top" />
          )}
        </div>
      </div>
    </PageLayout>
  );
}
